// frontend/src/pages/canvasser/WalkScreen.tsx
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, Flex, Spinner, Text, VStack } from '@chakra-ui/react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMyTurfs, useStopsDoneHere, useMarkStopDoneHere } from '../../hooks/useCanvasser'
import { useGrantCampaign } from '../../hooks/useGrantCampaign'
import { useGeolocation } from '../../hooks/useGeolocation'
import { useWalkRoute } from '../../hooks/useWalkRoute'
import { useReplanNudge } from '../../hooks/useReplanNudge'
import { useSync } from '../../providers/SyncProvider'
import { isCacheExpiredError } from '../../lib/offlineStore'
import {
  buildWalkStops, dockStopOnReturn, doorPath, firstOpenResident, isPersonFirstTurf, matchesFilter, matchesSearch, matchesSide,
  orderStops, placeMovedHomeStops, sequenceOf, stateRuleFor, stopSignature, walkProgress,
  type LeftStop, type StatusFilter, type WalkStop,
} from '../../lib/walkStops'
import { readLeftStop, readWalkPrefs, rememberLeftStop, writeWalkPrefs, type WalkPrefs } from '../../lib/walkPrefs'
import { formatDistance, haversineMeters, parseLine } from '../../lib/walkGeo'
import { publishWalkSnapshot } from '../../lib/walkSimBridge'
import { CacheExpiredNotice } from '../../components/canvasser/CacheExpiredNotice'
import { CanvasserMapView } from '../../components/canvasser/CanvasserMapView'
import { TodayBackLink } from '../../components/canvasser/TodayBackLink'
import { useOfflineMapSave } from '../../hooks/useOfflineMapSave'
import { OfflineMapBadge } from '../../components/canvasser/walk/OfflineMapBadge'
import { ReplanNudge } from '../../components/canvasser/walk/ReplanNudge'
import { WalkStrip } from '../../components/canvasser/walk/WalkStrip'
import { WalkFilterSheet } from '../../components/canvasser/walk/WalkFilterSheet'
import { WalkStopList } from '../../components/canvasser/walk/WalkStopList'
import { NextDoorDock } from '../../components/canvasser/walk/NextDoorDock'

const NO_KEYS: ReadonlySet<string> = new Set()

/**
 * [Spec D4/§4.3/§15.1 S1-S5] The one walk screen: List|Map over ONE ordered stop list, a pinned
 * next-door dock (tap a row or pin to put that door in it), turf-scoped progress, list-kind badge.
 * "I'm at the door" opens the Phase 4 door page for the docked stop (with the docked person on a contact walk).
 * [CF-1 P3] The turf comes from the home's Today choice (`turfId` prop); this screen never picks one.
 */
export function WalkScreen({ turfId: chosenTurfId, showTodayLink }: { turfId: string; showTodayLink: boolean }) {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const campaign = useGrantCampaign()
  const { isOnline } = useSync()
  const { data: turfs, isLoading, error } = useMyTurfs(campaign?.id)

  const [prefs, setPrefs] = useState<WalkPrefs>(() => {
    const p = readWalkPrefs()
    return params.get('view') === 'map' ? { ...p, surface: 'map' } : p
  })
  useEffect(() => { writeWalkPrefs(prefs) }, [prefs])
  // `?view=map` (the /canvasser/map redirect) is a one-shot: drop it once read, so returning from a
  // door doesn't force the map again over the canvasser's own surface choice.
  useEffect(() => {
    if (params.has('view')) {
      setParams(p => { const next = new URLSearchParams(p); next.delete('view'); return next }, { replace: true })
    }
  }, [params, setParams])
  const update = useCallback((patch: Partial<WalkPrefs>) => setPrefs(p => ({ ...p, ...patch })), [])

  const turf = turfs?.find(t => t.turfId === chosenTurfId)
  const turfId = turf?.turfId ?? ''
  const addressMode = turf?.addressMode ?? 'Home'
  const walkType = turf?.walkType ?? 'Walk'

  // Derived, not effect-synced: a Drive turf must route in driving mode from the first render (an
  // effect flip fired a wasted walking request first). The canvasser's toggle overrides per turf.
  type TravelMode = 'walking' | 'driving'
  const [modeOverride, setModeOverride] = useState<{ turfId: string; mode: TravelMode } | null>(null)
  const travelMode: TravelMode = modeOverride?.turfId === turfId
    ? modeOverride.mode
    : walkType === 'Drive' ? 'driving' : 'walking'
  const setTravelMode = (mode: TravelMode) => setModeOverride({ turfId, mode })

  const doneHere = useStopsDoneHere(turfId || undefined)
  const markDoneHere = useMarkStopDoneHere().mutate
  const stops = useMemo(
    () => (turf ? buildWalkStops(turf.contacts, addressMode, doneHere.data ?? NO_KEYS, stateRuleFor(turf.assignmentKind)) : []),
    [turf, addressMode, doneHere.data])

  const { position, recenter } = useGeolocation()
  const mapSaveStatus = useOfflineMapSave({ turfId, stops, mode: travelMode, isOnline })
  // Odd/even side of the street: narrows the route, list, dock, numbering and counts to this
  // canvasser's side. The map still gets every stop -- the other side falls outside visibleKeys and
  // renders faded, so a pair can see each other's progress without being routed across the street.
  const sideStops = useMemo(() => stops.filter(s => matchesSide(s, prefs.side)), [stops, prefs.side])
  const openStops = useMemo(() => sideStops.filter(s => s.state !== 'done'), [sideStops])
  const route = useWalkRoute({ stops: openStops, turfId, mode: travelMode, currentPosition: position, enabled: !!turf,
    orderIsManual: turf?.orderIsManual ?? false })
  // useWalkRoute keeps the previous route as placeholder data even while its query is disabled
  // (< 2 located open stops), so the route is only drawable when there really are 2+ waypoints.
  const routable = useMemo(() => openStops.filter(s => s.lat != null && s.lng != null).length >= 2, [openStops])
  const routeData = routable ? route.routeData : null
  const routeLine = useMemo(() => parseLine(routeData?.routeGeoJson), [routeData?.routeGeoJson])
  const nextSegmentLine = useMemo(() => parseLine(routeData?.nextSegmentGeoJson), [routeData?.nextSegmentGeoJson])

  // Stops moved home today are cheapest-inserted next to their nearest stops — unless the route order
  // already places them (R26), so an optimized route never has a stop pulled out of it.
  const ordered = useMemo(
    () => placeMovedHomeStops(orderStops(stops, route.order), new Set(route.order ?? [])),
    [stops, route.order])
  const sideOrdered = useMemo(() => ordered.filter(s => matchesSide(s, prefs.side)), [ordered, prefs.side])
  const sequence = useMemo(() => sequenceOf(sideOrdered), [sideOrdered])
  const [searchText, setSearchText] = useState('')
  const visible = useMemo(
    () => sideOrdered.filter(s => matchesFilter(s, prefs.filter) && matchesSearch(s, searchText)),
    [sideOrdered, prefs.filter, searchText])
  const visibleKeys = useMemo(
    () => (prefs.filter === 'all' && prefs.side === 'all' && !searchText.trim() ? null : new Set(visible.map(s => s.key))),
    [prefs.filter, prefs.side, searchText, visible])
  const counts = useMemo<Record<StatusFilter, number>>(() => ({
    remaining: sideStops.filter(s => matchesFilter(s, 'remaining')).length,
    revisit: sideStops.filter(s => matchesFilter(s, 'revisit')).length,
    all: sideStops.length,
  }), [sideStops])
  const progress = useMemo(() => walkProgress(sideStops), [sideStops])

  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  /** Contacts view: the person tapped (not just their stop), so the dock names and opens THEM (I-5). */
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null)
  useEffect(() => { setSelectedKey(null); setSelectedContactId(null) }, [turfId])
  const onSelect = useCallback((key: string, contactId?: string) => {
    setSelectedKey(key)
    setSelectedContactId(contactId ?? null)
  }, [])
  // [S5] The stop the canvasser last left from: read from sessionStorage once per turf, then held in
  // state so "Done here" can move it on without a round-trip through the door page (I-1).
  const storedLeft = useMemo(() => (turfId ? readLeftStop(turfId) : null), [turfId])
  const [leftOverride, setLeftOverride] = useState<{ turfId: string; left: LeftStop } | null>(null)
  const left = leftOverride?.turfId === turfId ? leftOverride.left : storedLeft
  const suggested = useMemo(() => dockStopOnReturn(sideOrdered, left), [sideOrdered, left])
  const selected = selectedKey ? ordered.find(s => s.key === selectedKey) ?? null : null
  const dockStop = selected ?? suggested
  // Person-first dock: a contact walk (doorStyle), or the Contacts view as a display option. DoorPage
  // itself decides the door FLOW from doorStyle (P4-1).
  const personFirst = isPersonFirstTurf(turf) || prefs.viewMode === 'contacts'
  // Person-first: the person the dock names and "I'm at the door" opens — the tapped person when
  // they belong to the docked stop, else its first open resident. The list presses that same row.
  const dockPersonId = personFirst && dockStop
    ? (selected && selectedContactId && dockStop.residents.some(r => r.contactId === selectedContactId)
        ? selectedContactId
        : firstOpenResident(dockStop).contactId)
    : null

  // DEV only: feed the walk simulator the docked target + next-segment line (absent from prod builds).
  const dockKey = dockStop?.key ?? null
  const dockLat = dockStop?.lat ?? null
  const dockLng = dockStop?.lng ?? null
  const segmentCoords = nextSegmentLine?.geometry.coordinates
  const routeState = route.routeState
  // [A3 §6] Off-route measures ONLY against a road line the server drew. Unrouted (no geometry) there is
  // no line — a straight line through open stops falsely nags at finished doors — and over the optimize
  // cap ⚡ can't re-plan anyway, so both pass null (no off-route nudge).
  const overCap = route.optimizeBlockedReason === 'tooMany'
  const nudgeLine = useMemo<[number, number][] | null>(() => {
    if (overCap) return null
    const drawn = routeLine?.geometry.coordinates as [number, number][] | undefined
    return drawn && drawn.length >= 2 ? drawn : null
  }, [routeLine, overCap])
  const { nudge, dismiss: dismissNudge, reset: resetNudge } = useReplanNudge({
    scope: `${turfId}:${travelMode}`, routeState, routingBack: route.routingBack, routeLine: nudgeLine,
    position, mode: travelMode, locked: route.orderLocked,
  })
  useEffect(() => {
    if (import.meta.env.DEV) {
      publishWalkSnapshot({
        turfId, mode: travelMode,
        target: dockKey != null && dockLat != null && dockLng != null ? { key: dockKey, lat: dockLat, lng: dockLng } : null,
        segment: (segmentCoords as [number, number][] | undefined) ?? null,
        routeState,
      })
    }
  }, [turfId, travelMode, dockKey, dockLat, dockLng, segmentCoords, routeState])

  // Every Re-plan (button or banner) re-arms the nudge, even when the re-freeze leaves routeState unchanged.
  const { reoptimize } = route
  const onReplan = useCallback(() => { resetNudge(); reoptimize() }, [resetNudge, reoptimize])

  const [sheetOpen, setSheetOpen] = useState(false)

  const firstOpen = sideOrdered.find(s => s.state !== 'done')
  const distanceLabel = (() => {
    if (!dockStop) return ''
    if (dockStop.key === firstOpen?.key && routeData?.nextSegmentDistanceMeters != null)
      return formatDistance(routeData.nextSegmentDistanceMeters)
    if (position && dockStop.lat != null && dockStop.lng != null)
      return `≈ ${formatDistance(haversineMeters(position.lat, position.lng, dockStop.lat, dockStop.lng))}`
    return ''
  })()

  const onAtDoor = (stop: WalkStop, contactId: string) => {
    rememberLeftStop(turfId, { key: stop.key, state: stop.state, sig: stopSignature(stop) })
    navigate(doorPath(turfId, stop.key, personFirst ? contactId : null))
  }
  const onDoneHere = (stop: WalkStop) => {
    markDoneHere({ turfId, stopKey: stop.key })
    // Leave from it as if recorded: once its marker lands `visitedToday` flips, the signature changes,
    // and dockStopOnReturn advances to the stop AFTER it (S5) instead of back to the first open one.
    const leftHere: LeftStop = { key: stop.key, state: stop.state, sig: stopSignature(stop) }
    rememberLeftStop(turfId, leftHere)
    setLeftOverride({ turfId, left: leftHere })
    setSelectedKey(null)
    setSelectedContactId(null)
  }
  const hasNarrowing = prefs.filter !== 'all' || prefs.side !== 'all' || !!searchText
  const showAll = useCallback(() => { update({ filter: 'all', side: 'all' }); setSearchText('') }, [update])
  const emptyMessage = searchText.trim() ? 'No matches' : prefs.filter === 'revisit' ? 'Nothing to revisit' : 'Nothing left here'

  if (isLoading) return <VStack py={16}><Spinner size="lg" color="accent" /></VStack>
  if (error) {
    if (isCacheExpiredError(error)) return <CacheExpiredNotice />
    return (
      <Box p={4}>
        <Alert.Root status="error" borderRadius="md"><Alert.Indicator />We couldn't load your list. Try again in a moment.</Alert.Root>
      </Box>
    )
  }
  if (!turf) return null // CampaignHomePage shows the waiting room when there is no turf

  const statusLabel = searchText.trim() ? `Search: "${searchText.trim()}" (${visible.length})`
    : prefs.filter === 'all' ? null : `${prefs.filter === 'revisit' ? 'Revisit' : 'Remaining'} (${visible.length})`
  const sideLabel = prefs.side === 'all' ? null : prefs.side === 'odd' ? 'Odd side' : 'Even side'
  const filterLabel = [sideLabel, statusLabel].filter(Boolean).join(' · ') || null

  return (
    <Flex direction="column" h="100%" minH={0}>
      {showTodayLink && <Box px={2} flexShrink={0}><TodayBackLink /></Box>}
      <WalkStrip addressMode={addressMode} walkType={walkType} progress={progress}
        peopleCount={prefs.viewMode === 'contacts' ? turf?.contacts.length : undefined}
        surface={prefs.surface} onSurfaceChange={surface => update({ surface })}
        filter={prefs.filter} onOpenFilters={() => setSheetOpen(true)} />

      <Box flex={1} minH={0} position="relative" overflowY={prefs.surface === 'list' ? 'auto' : 'hidden'}>
        {prefs.surface === 'list' ? (
          <Box px={4} py={3}>
            <WalkStopList stops={visible} sequence={sequence} viewMode={prefs.viewMode}
              selectedKey={dockStop?.key ?? null} selectedContactId={dockPersonId} onSelect={onSelect}
              showKindIcons={addressMode === 'Hybrid'}
              emptyMessage={emptyMessage}
              onShowAll={hasNarrowing ? showAll : undefined} />
          </Box>
        ) : (
          <>
          <CanvasserMapView stops={ordered}
            visibleKeys={visibleKeys}
            sequence={sequence} selectedKey={dockStop?.key ?? null} onSelectStop={setSelectedKey}
            activeFilterLabel={filterLabel}
            routeGeoJson={routeLine}
            nextSegmentGeoJson={nextSegmentLine}
            gpsPosition={position}
            nextDistance={routeData?.nextSegmentDistanceMeters ?? null}
            nextDuration={routeData?.nextSegmentDurationSeconds ?? null}
            travelMode={travelMode} onTravelModeChange={setTravelMode}
            orderLocked={route.orderLocked} onReoptimize={onReplan} isRouteOptimized={route.isRouteOptimized}
            optimizeBlockedReason={route.optimizeBlockedReason ?? null}
            routingUnavailable={route.routingUnavailable} onRecenter={recenter} isRefreshing={route.isFetching}
            showKindIcons={addressMode === 'Hybrid'}
            routeState={routeState} replanTipSeen={prefs.replanTipSeen}
            onDismissReplanTip={() => update({ replanTipSeen: true })}
            topStatus={<>
              <OfflineMapBadge status={mapSaveStatus} />
              {!isOnline && (mapSaveStatus === 'notSaved' || mapSaveStatus === 'partial' || travelMode === 'driving') && (
                <VStack pointerEvents="auto" w="100%" gap={2} p={3} bg="surface" borderRadius="md" shadow="md">
                  <Text textAlign="center" color="fg.muted">Map isn't saved for this area. Your list works offline.</Text>
                  <Button minH="44px" onClick={() => update({ surface: 'list' })}>Show the list</Button>
                </VStack>
              )}
            </>} />
          <ReplanNudge nudge={nudge} onReplan={onReplan} onDismiss={dismissNudge} />
          </>
        )}
      </Box>

      <NextDoorDock stop={dockStop} sequenceNumber={dockStop ? sequence.get(dockStop.key) ?? null : null}
        isSuggested={!selected} distanceLabel={distanceLabel} travelMode={travelMode}
        personFirst={personFirst} personId={dockPersonId} revisitCount={progress.revisit}
        onAtDoor={onAtDoor}
        onDoneHere={onDoneHere}
        onShowRevisits={() => update({ filter: 'revisit', surface: 'list' })} />

      <WalkFilterSheet open={sheetOpen} onClose={() => setSheetOpen(false)}
        filter={prefs.filter} onFilterChange={filter => update({ filter })} counts={counts}
        viewMode={prefs.viewMode} onViewModeChange={viewMode => update({ viewMode })}
        searchText={searchText} onSearchTextChange={setSearchText}
        side={prefs.side} onSideChange={side => update({ side })} />
    </Flex>
  )
}
